import { Box, Grid, Link } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import { BlockAnimationWithImageDescriptionData } from './BlockAnimationWithImageDescriptionData';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import CroppedImage from '../../CroppedImage';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useGetBackground from '../../../hooks/UseGetBackground';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

const BlockAnimationWithImageDescription = (props: BlockProps) => {
  const {
    isEditing, isTemplate, data, editHandler, showImageModal, onCloseImageModal, previewMode,
  } = props;

  const targetModalId = uuid();
  const imageBreakpoint = previewMode === 'smartphone' ? 12 : 5;

  const imageMedias = previewMode !== 'smartphone' ? {
    '@media (max-width: 734px)': {
      minWidth: '100% !important',
    },
    '@media (min-width: 734px)': {
      flexBasis: '41.666667%',
      flexGrow: 0,
      maxWidth: '100%',
    },
  } : {
    minWidth: '100% !important',
  };

  const [blockData, setBlockData] = useState<BlockAnimationWithImageDescriptionData>(data.data as BlockAnimationWithImageDescriptionData);

  const {
    title, imgLeft, imgRight, imgLeftDesc, imgRightDesc, text1, text2,
  } = blockData;

  const { id } = data;
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const imgLeftRef = useRef<HTMLDivElement>(null);
  const imgRightRef = useRef<HTMLDivElement>(null);
  const text1Ref = useRef<HTMLDivElement>(null);
  const text2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { current } = titleRef;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', 'animation-block-slide-down');
    }
  }, []);

  useEffect(() => {
    const { current } = imgLeftRef;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', 'animation-block-slide-from-left');
    }
  }, []);

  useEffect(() => {
    const { current } = imgRightRef;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', 'animation-block-slide-from-right');
    }
  }, []);

  useEffect(() => {
    const { current } = text1Ref;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', 'animation-block-slide-up');
    }
  }, []);

  useEffect(() => {
    const { current } = text2Ref;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', 'animation-block-slide-up');
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        //  entriesは観察された要素の配列です。
        if (entries[0].isIntersecting) {
          // >= 0.7は最初画面に表示される時に
          // >= 0.1は下にスクロールした時に
          setIsInView(entries[0].intersectionRatio > 0.1 && entries[0].intersectionRatio < 0.7);
          observer.unobserve(containerRef.current);
        }
      },
      {
        root: null, // ビューポートを使用しています。
        threshold: 0.3, // 要素の30%が表示された時にコールバックを実行します。
      },
    );

    const { current } = containerRef;

    if (current) {
      observer.observe(current);
    }

    // コンポーネントのアンマウント時にオブザーバーをクリーンアップします。
    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [containerRef]);

  const elementUuid = uuid();
  useParallaxScript(blockData.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        flexDirection="column"
        gap={3}
        ref={containerRef}
        sx={{
          ...useGetBackground(blockData.backgroundImage?.solidColor, blockData.backgroundImage?.value, blockData.backgroundImage?.settings?.scale, blockData.backgroundImage?.settings?.scaleBool),
          backgroundPosition: 'center 0px',
        }}
      >

        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={blockData.backgroundImage}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(backgroundImage: BlockComponentImage) => {
              if (!blockData.backgroundImage) {
                setBlockData({ ...blockData, backgroundImage: new BlockComponentImage() });
              }
              setBlockData({ ...blockData, backgroundImage });
              editHandler(data.id, { ...blockData, backgroundImage });
            }}
          />
        )}
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="block-content-wrapper"
        >
          {title && (
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
              ref={titleRef}
              className={`block-content-wrapper ${(isInView && !isEditing) ? 'animation-block-slide-down' : ''} mt-4`}
              sx={{
                overflow: 'hidden',
                backgroundPosition: 'center 0px',
              }}
            >
              <Grid
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                className="block-content-wrapper"
                style={{
                  margin: '0 !important',
                }}
              >
                <Grid item sx={Utils.hoverBorder(isEditing)}>
                  {isEditing && (
                    <CloseButton updateHandler={() => {
                      delete blockData.title;
                      editHandler(id, blockData);
                    }}
                    />
                  )}

                  <EditableText
                    text={title.value}
                    textColor={title.settings?.textColor}
                    isEditable={isEditing && !isTemplate}
                    updateHandler={(text) => {
                      blockData.title.value = text;
                      editHandler(id, blockData);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid
            item
            container
            className={`${isEditing ? 'mt-4' : 'mt-3'} animation-parent-container`}
            flexDirection="row"
            justifyContent="space-evenly"
            rowGap={2}
          >
            <Grid className="animation-container" item gap={2} flexDirection="column" sx={{ width: '100%' }}>
              {imgLeft && (
                <Grid
                  item
                  ref={imgLeftRef}
                  className={(isInView && !isEditing) ? 'animation-block-slide-from-left' : ''}
                  md={imageBreakpoint}
                  sx={{
                    ...Utils.hoverBorder(isEditing),
                    ...imageMedias,
                  }}
                >
                  {isEditing && (
                    <CloseButton updateHandler={() => {
                      delete blockData.imgLeft;
                      editHandler(id, blockData);
                    }}
                    />
                  )}
                  <ImageSettingsModal
                    isEditing={isEditing}
                    blockData={[data]}
                    image={imgLeft}
                    disabled={isTemplate || !isEditing}
                    onConfirm={(newImage: BlockComponentImage) => {
                      blockData.imgLeft = newImage;
                      editHandler(data.id, blockData);
                    }}
                  >
                    {(!isEditing) ? (
                      <Link
                        style={{ cursor: imgLeft?.link?.linkData?.url ? 'pointer' : 'default' }}
                        target={imgLeft?.link?.linkData?.openType}
                        href={imgLeft?.link?.linkData?.url || undefined}
                        sx={{
                          width: '100%',
                          height: '100%',
                        }}
                        rel="noreferrer"
                      >
                        <CroppedImage
                          image={imgLeft}
                          isEditing={isEditing}
                          settings={imgLeft.settings}
                          targetModalId={targetModalId}
                        />
                      </Link>
                    ) : (
                      <CroppedImage
                        image={imgLeft}
                        isEditing={isEditing}
                        settings={imgLeft.settings}
                        targetModalId={targetModalId}
                      />
                    )}
                  </ImageSettingsModal>
                </Grid>
              )}

              {imgLeftDesc && (
                <Grid
                  className="mt-3"
                  container
                  item
                  xs={12}
                  justifyContent="center"
                >
                  <Box sx={Utils.hoverBorder(isEditing)} width="100%">
                    {isEditing && (
                      <CloseButton updateHandler={() => {
                        delete blockData.imgLeftDesc;
                        editHandler(id, blockData);
                      }}
                      />
                    )}
                    <EditableText
                      text={imgLeftDesc.value}
                      textColor={imgLeftDesc.settings?.textColor}
                      isEditable={isEditing && !isTemplate}
                      updateHandler={(text) => {
                        blockData.imgLeftDesc.value = text;
                        editHandler(id, blockData);
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Grid className="animation-container" item gap={2} flexDirection="column" sx={{ width: '100%' }}>
              {imgRight && (
                <Grid
                  item
                  ref={imgRightRef}
                  className={(isInView && !isEditing) ? 'animation-block-slide-from-right' : ''}
                  md={imageBreakpoint}
                  sx={{
                    ...Utils.hoverBorder(isEditing),
                    ...imageMedias,
                  }}
                >
                  {isEditing && (
                    <CloseButton updateHandler={() => {
                      delete blockData.imgRight;
                      editHandler(id, blockData);
                    }}
                    />
                  )}
                  <ImageSettingsModal
                    isEditing={isEditing}
                    blockData={[data]}
                    image={imgRight}
                    disabled={isTemplate || !isEditing}
                    onConfirm={(newImage: BlockComponentImage) => {
                      blockData.imgRight = newImage;
                      editHandler(data.id, blockData);
                    }}
                  >
                    {(!isEditing) ? (
                      <Link
                        style={{ cursor: imgRight?.link?.linkData?.url ? 'pointer' : 'default' }}
                        target={imgRight?.link?.linkData?.openType}
                        href={imgRight?.link?.linkData?.url || undefined}
                        sx={{
                          width: '100%',
                          height: '100%',
                        }}
                        rel="noreferrer"
                      >
                        <CroppedImage
                          image={imgRight}
                          isEditing={isEditing}
                          settings={imgRight.settings}
                          targetModalId={targetModalId}
                        />
                      </Link>
                    ) : (
                      <CroppedImage
                        image={imgRight}
                        isEditing={isEditing}
                        settings={imgRight.settings}
                        targetModalId={targetModalId}
                      />
                    )}
                  </ImageSettingsModal>
                </Grid>
              )}
              {imgRightDesc && (
                <Grid
                  className="mt-3"
                  container
                  item
                  xs={12}
                  justifyContent="center"
                >
                  <Box sx={Utils.hoverBorder(isEditing)} width="100%">
                    {isEditing && (
                      <CloseButton updateHandler={() => {
                        delete blockData.imgRightDesc;
                        editHandler(id, blockData);
                      }}
                      />
                    )}
                    <EditableText
                      text={imgRightDesc.value}
                      textColor={imgRightDesc.settings?.textColor}
                      isEditable={isEditing && !isTemplate}
                      updateHandler={(text) => {
                        blockData.imgRightDesc.value = text;
                        editHandler(id, blockData);
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid
            item
            container
            gap={3}
            ref={text1Ref}
            className={(isInView && !isEditing) ? 'animation-block-slide-up' : ''}
            flexDirection="column"
            alignContent="space-around"
            style={{
              margin: '0 0 34px 0',
            }}
          >
            {text1 && (
              <Grid
                className="mt-3"
                container
                item
                xs={12}
                justifyContent="center"
              >
                <Box sx={Utils.hoverBorder(isEditing)} width="90%">
                  {isEditing && (
                    <CloseButton updateHandler={() => {
                      delete blockData.text1;
                      editHandler(id, blockData);
                    }}
                    />
                  )}
                  <EditableText
                    text={text1.value}
                    textColor={text1.settings?.textColor}
                    isEditable={isEditing && !isTemplate}
                    updateHandler={(text) => {
                      blockData.text1.value = text;
                      editHandler(id, blockData);
                    }}
                  />
                </Box>
              </Grid>
            )}
            {text2 && (
              <Grid
                container
                item
                xs={12}
                flexDirection="row"
                justifyContent="center"
                ref={text2Ref}
              >
                <Box sx={Utils.hoverBorder(isEditing)} width="90%">
                  {isEditing && (
                    <CloseButton updateHandler={() => {
                      delete blockData.text2;
                      editHandler(id, blockData);
                    }}
                    />
                  )}
                  <EditableText
                    text={text2.value}
                    textColor={text2.settings?.textColor}
                    isEditable={isEditing && !isTemplate}
                    updateHandler={(text) => {
                      blockData.text2.value = text;
                      editHandler(id, blockData);
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ImageEnlargeModal
        targetModalId={targetModalId}
        images={[imgLeft, imgRight]}
      />
    </>
  );
};

export default BlockAnimationWithImageDescription;
